import { User } from '../types/schema';
import { Role, getRoleRank, resolveRole } from './permissions';

export type OrgChartPerson = {
    id: string;
    name: string;
    email?: string | null;
    role: Role | null;
    roleLabel: string;
    position?: string | null;
    userPhoto?: string | null;
    supervisorName?: string | null;
};

export type OrgChartNode = OrgChartPerson & {
    children: OrgChartNode[];
    isFocus: boolean;
    isPrimaryRoot: boolean;
};

export type OrgChartCounts = {
    total: number;
    superAdmin: number;
    admin: number;
    foreman: number;
    inspector: number;
    serviceTechnician: number;
    unknown: number;
};

export type BuildOrgChartOptions = {
    companyOwnerId?: string | null;
    focusUserId?: string | null;
};

const ROLE_LABELS: Record<Role, string> = {
    [Role.SUPER_ADMIN]: 'Super Admin',
    [Role.ADMIN]: 'Admin',
    [Role.FOREMAN]: 'Foreman',
    [Role.INSPECTOR]: 'Inspector',
    [Role.SERVICE_TECHNICIAN]: 'Service Technician',
};

export function formatOrgChartRoleLabel(role?: string | null): string {
    const resolved = resolveRole(role);
    if (!resolved) return role?.trim() || 'Unassigned';
    return ROLE_LABELS[resolved];
}

export function canViewOrgChartTab(viewer: {
    businessAdmin?: unknown | null;
    companyOwner?: unknown | null;
    role?: string | null;
}): boolean {
    if (viewer.businessAdmin) return true;
    return resolveRole(viewer.role) === Role.SUPER_ADMIN;
}

function toPerson(user: User): OrgChartPerson | null {
    if (!user?.id) return null;
    const role = resolveRole(user.role);
    return {
        id: user.id,
        name: (user.name || user.email || 'Unnamed').trim(),
        email: user.email,
        role,
        roleLabel: formatOrgChartRoleLabel(user.role),
        position: user.position,
        userPhoto: user.userPhoto,
        supervisorName: user.inputByPerson?.trim() || null,
    };
}

function sortPeople(a: OrgChartPerson, b: OrgChartPerson): number {
    const rankDiff = getRoleRank(b.role) - getRoleRank(a.role);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

function pickPrimary(
    candidates: OrgChartPerson[],
    companyOwnerId?: string | null,
): OrgChartPerson | null {
    if (candidates.length === 0) return null;
    if (companyOwnerId) {
        const owner = candidates.find((p) => p.id === companyOwnerId);
        if (owner) return owner;
    }
    return [...candidates].sort(sortPeople)[0];
}

function findDefaultParentId(
    person: OrgChartPerson,
    primaryByRank: {
        superAdmin: OrgChartPerson | null;
        admin: OrgChartPerson | null;
        foreman: OrgChartPerson | null;
    },
): string | null {
    const rank = getRoleRank(person.role);

    if (rank >= getRoleRank(Role.SUPER_ADMIN)) {
        return null;
    }
    if (rank >= getRoleRank(Role.ADMIN)) {
        return primaryByRank.superAdmin?.id ?? null;
    }
    if (rank >= getRoleRank(Role.FOREMAN)) {
        return primaryByRank.admin?.id ?? primaryByRank.superAdmin?.id ?? null;
    }
    // Field staff
    return (
        primaryByRank.foreman?.id ??
        primaryByRank.admin?.id ??
        primaryByRank.superAdmin?.id ??
        null
    );
}

function resolveSupervisorParentId(
    person: OrgChartPerson,
    people: OrgChartPerson[],
): string | null {
    const supervisor = person.supervisorName?.trim().toLowerCase();
    if (!supervisor) return null;

    const matches = people.filter(
        (candidate) =>
            candidate.id !== person.id &&
            candidate.name.trim().toLowerCase() === supervisor,
    );
    if (matches.length !== 1) return null;

    const parent = matches[0];
    // Only allow reporting up (or equal rank sibling under higher person via soft match)
    if (getRoleRank(parent.role) < getRoleRank(person.role)) return null;
    return parent.id;
}

function attachChild(parentMap: Map<string, string[]>, parentId: string, childId: string) {
    const list = parentMap.get(parentId) || [];
    if (!list.includes(childId)) {
        list.push(childId);
        parentMap.set(parentId, list);
    }
}

function buildNode(
    person: OrgChartPerson,
    childrenByParent: Map<string, string[]>,
    peopleById: Map<string, OrgChartPerson>,
    focusUserId: string | null | undefined,
    primaryRootId: string | null,
    visiting: Set<string>,
): OrgChartNode {
    if (visiting.has(person.id)) {
        return {
            ...person,
            children: [],
            isFocus: person.id === focusUserId,
            isPrimaryRoot: person.id === primaryRootId,
        };
    }
    visiting.add(person.id);
    const childIds = childrenByParent.get(person.id) || [];
    const children = childIds
        .map((id) => peopleById.get(id))
        .filter((p): p is OrgChartPerson => Boolean(p))
        .sort(sortPeople)
        .map((child) =>
            buildNode(child, childrenByParent, peopleById, focusUserId, primaryRootId, visiting),
        );
    visiting.delete(person.id);

    return {
        ...person,
        children,
        isFocus: person.id === focusUserId,
        isPrimaryRoot: person.id === primaryRootId,
    };
}

export function countOrgChartRoles(people: OrgChartPerson[]): OrgChartCounts {
    const counts: OrgChartCounts = {
        total: people.length,
        superAdmin: 0,
        admin: 0,
        foreman: 0,
        inspector: 0,
        serviceTechnician: 0,
        unknown: 0,
    };

    for (const person of people) {
        switch (person.role) {
            case Role.SUPER_ADMIN:
                counts.superAdmin += 1;
                break;
            case Role.ADMIN:
                counts.admin += 1;
                break;
            case Role.FOREMAN:
                counts.foreman += 1;
                break;
            case Role.INSPECTOR:
                counts.inspector += 1;
                break;
            case Role.SERVICE_TECHNICIAN:
                counts.serviceTechnician += 1;
                break;
            default:
                counts.unknown += 1;
        }
    }

    return counts;
}

/**
 * Build a company org chart tree from User directory roles.
 */
export function buildOrgChartTree(
    users: User[],
    options: BuildOrgChartOptions = {},
): { roots: OrgChartNode[]; people: OrgChartPerson[]; counts: OrgChartCounts } {
    const people = users
        .map(toPerson)
        .filter((p): p is OrgChartPerson => Boolean(p))
        .sort(sortPeople);

    const peopleById = new Map(people.map((p) => [p.id, p]));
    const byRole = {
        superAdmin: people.filter((p) => p.role === Role.SUPER_ADMIN),
        admin: people.filter((p) => p.role === Role.ADMIN),
        foreman: people.filter((p) => p.role === Role.FOREMAN),
    };

    const primaryByRank = {
        superAdmin: pickPrimary(byRole.superAdmin, options.companyOwnerId),
        admin: pickPrimary(byRole.admin, options.companyOwnerId),
        foreman: pickPrimary(byRole.foreman, options.companyOwnerId),
    };

    const parentOf = new Map<string, string>();
    const childrenByParent = new Map<string, string[]>();

    for (const person of people) {
        const supervisorParent = resolveSupervisorParentId(person, people);
        const defaultParent = findDefaultParentId(person, primaryByRank);
        const parentId = supervisorParent || defaultParent;
        if (!parentId || parentId === person.id) continue;
        // Prevent cycles: parent must not already be under this person
        parentOf.set(person.id, parentId);
        attachChild(childrenByParent, parentId, person.id);
    }

    // Break simple cycles if soft-match created one
    for (const [childId, parentId] of parentOf) {
        let cursor: string | undefined = parentId;
        const seen = new Set<string>([childId]);
        let cyclic = false;
        while (cursor) {
            if (seen.has(cursor)) {
                cyclic = true;
                break;
            }
            seen.add(cursor);
            cursor = parentOf.get(cursor);
        }
        if (cyclic) {
            const list = childrenByParent.get(parentId) || [];
            childrenByParent.set(
                parentId,
                list.filter((id) => id !== childId),
            );
            parentOf.delete(childId);
        }
    }

    const childIds = new Set([...parentOf.keys()]);
    let roots = people.filter((p) => !childIds.has(p.id));

    // Prefer company owner / primary super admin first among roots
    const primaryRootId =
        (options.companyOwnerId && peopleById.has(options.companyOwnerId)
            ? options.companyOwnerId
            : primaryByRank.superAdmin?.id) || roots[0]?.id || null;

    roots = roots.sort((a, b) => {
        if (a.id === primaryRootId) return -1;
        if (b.id === primaryRootId) return 1;
        return sortPeople(a, b);
    });

    const treeRoots = roots.map((root) =>
        buildNode(
            root,
            childrenByParent,
            peopleById,
            options.focusUserId,
            primaryRootId,
            new Set(),
        ),
    );

    return {
        roots: treeRoots,
        people,
        counts: countOrgChartRoles(people),
    };
}

export function countDirectReports(node: OrgChartNode): number {
    return node.children.length;
}

export function countSubtree(node: OrgChartNode): number {
    return node.children.reduce((sum, child) => sum + 1 + countSubtree(child), 0);
}
